import React, { useRef, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { InventoryCompound } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';
import StructureImage from '../chemistry/StructureImage';
import { formatDate } from '../../utils/dateFormat';

interface Props {
  onCompoundClick: (compound: InventoryCompound) => void;
  onEditStock: (compound: InventoryCompound) => void;
}

// Structure cell renderer — uses cached PNG or SVG fallback via StructureImage
const StructureCellRenderer: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
  return <StructureImage smiles={value} size={80} lazy />;
};

// IP Status dot
const IpStatusRenderer: React.FC<{ value: string }> = ({ value }) => {
  const colorMap: Record<string, string> = {
    blocked: '#EF4444',
    caution: '#F59E0B',
    clear: '#10B981',
    unknown: '#6B7280',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        backgroundColor: colorMap[value] || colorMap.unknown,
      }} />
      <span style={{ fontSize: 12, textTransform: 'capitalize' }}>{value || 'unknown'}</span>
    </div>
  );
};

// Category badge
const CATEGORY_COLORS: Record<string, string> = {
  compound: '#0D9488',
  reagent: '#8B5CF6',
  building_block: '#F59E0B',
  consumable: '#6B7280',
  biological: '#EC4899',
  external: '#6366F1',
};

const CategoryRenderer: React.FC<{ value: string }> = ({ value }) => (
  <span style={{
    padding: '2px 8px', borderRadius: 10,
    backgroundColor: `${CATEGORY_COLORS[value] || '#6B7280'}15`,
    color: CATEGORY_COLORS[value] || '#6B7280',
    fontSize: 11, fontWeight: 500,
    textTransform: 'capitalize',
  }}>
    {(value || 'compound').replace(/_/g, ' ')}
  </span>
);

const InventoryCompoundGrid: React.FC<Props> = ({ onCompoundClick, onEditStock }) => {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs = useMemo(() => ([
    {
      headerName: 'IP',
      field: 'ip_status',
      width: 100,
      cellRenderer: IpStatusRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Code',
      field: 'chemistry_code',
      width: 120,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: { fontWeight: 600, color: '#0D9488', cursor: 'pointer' },
    },
    {
      headerName: 'Registry',
      field: 'registry_code',
      width: 120,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: { fontWeight: 500, color: '#3B82F6' },
      valueFormatter: (p: any) => p.value || '-',
    },
    {
      headerName: 'Type',
      field: 'category',
      width: 110,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CategoryRenderer,
    },
    {
      headerName: 'Structure',
      field: 'canonical_smiles',
      width: 180,
      cellRenderer: StructureCellRenderer,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Name',
      field: 'compound_name',
      width: 160,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Formula',
      field: 'mol_formula',
      width: 120,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'MW',
      field: 'mol_weight',
      width: 90,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (p: any) => p.value ? p.value.toFixed(1) : '-',
    },
    {
      headerName: 'Stock',
      field: 'total_stock',
      width: 90,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (p: any) => p.value != null ? `${p.value.toFixed(1)} mg` : '-',
      cellStyle: (p: any) => ({
        color: p.value != null && p.value < 10 ? '#EF4444' : 'var(--text-primary)',
        fontWeight: p.value != null && p.value < 10 ? 600 : 400,
      }),
    },
    {
      headerName: 'Batches',
      field: 'batch_count',
      width: 80,
      sortable: true,
    },
    {
      headerName: 'Samples',
      field: 'sample_count',
      width: 80,
      sortable: true,
    },
    {
      headerName: 'Patents',
      field: 'patent_count',
      width: 80,
      sortable: true,
      cellStyle: (p: any) => ({
        color: p.value > 0 ? '#EF4444' : 'var(--text-secondary)',
        fontWeight: p.value > 0 ? 600 : 400,
      }),
    },
    {
      headerName: 'Bioactivity',
      field: 'bioactivity_count',
      width: 90,
      sortable: true,
      cellStyle: (p: any) => ({
        color: p.value > 0 ? '#0D9488' : 'var(--text-secondary)',
      }),
    },
    {
      headerName: 'Targets',
      field: 'target_count',
      width: 80,
      sortable: true,
    },
    {
      headerName: 'Status',
      field: 'compound_status',
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: { textTransform: 'capitalize' as any },
    },
    {
      headerName: 'Actions',
      width: 120,
      minWidth: 120,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellRenderer: (params: any) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (params.data) onEditStock(params.data);
          }}
          style={{
            padding: '7px 12px', borderRadius: 5, border: '1px solid #0D9488',
            backgroundColor: '#0D9488', color: '#fff', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          }}
        >
          Edit stock
        </button>
      ),
    },
    {
      headerName: 'Registered',
      field: 'registered_at',
      width: 140,
      sortable: true,
      valueFormatter: (p: any) => p.value ? formatDate(p.value) : '-',
    },
  ] as ColDef[]), [onEditStock]);

  const datasource = useMemo<IServerSideDatasource>(() => ({
    getRows: async (params: IServerSideGetRowsParams) => {
      try {
        const { startRow, endRow, sortModel, filterModel } = params.request;
        const res = await inventoryAPI.queryCompounds({
          startRow: startRow || 0,
          endRow: endRow || 100,
          sortModel: sortModel as any,
          filterModel: filterModel as any,
        });
        params.success({
          rowData: res.data.rows,
          rowCount: res.data.lastRow,
        });
      } catch {
        params.fail();
      }
    },
  }), []);

  const onRowClicked = useCallback((event: any) => {
    if (event.data) {
      onCompoundClick(event.data);
    }
  }, [onCompoundClick]);

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    suppressMovable: false,
  }), []);

  return (
    <div style={{ width: '100%', height: '100%' }} className="ag-theme-alpine">
      <AgGridReact
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowModelType="serverSide"
        serverSideDatasource={datasource}
        cacheBlockSize={100}
        rowHeight={90}
        headerHeight={36}
        animateRows={true}
        pagination={true}
        paginationPageSize={50}
        onRowClicked={onRowClicked}
        rowStyle={{ cursor: 'pointer' }}
        overlayNoRowsTemplate='<span style="padding: 20px; color: var(--text-secondary)">No compounds registered yet. Use the Register tab to add your first compound.</span>'
      />
    </div>
  );
};

export default InventoryCompoundGrid;
